import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MetaTags from '../components/SEO/MetaTags';
import {
  CalculatorIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon,
  CurrencyRupeeIcon,
  UserGroupIcon,
  GlobeAltIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ScaleIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const Services = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: CalculatorIcon,
      title: 'Tax Planning & Filing',
      description: 'Comprehensive tax solutions for individuals and businesses',
      shortDescription: 'Strategic tax planning to minimize liabilities and maximize returns',
      features: [
        'Income Tax Return Filing',
        'Tax Planning & Strategy',
        'Tax Audit & Compliance',
        'Appeals & Assessments',
        'TDS & TCS Management',
        'Advance Tax Calculations',
      ],
    },
    {
      icon: DocumentTextIcon,
      title: 'GST Compliance',
      description: 'End-to-end GST services for seamless business operations',
      shortDescription: 'Complete GST lifecycle management from registration to filing',
      features: [
        'GST Registration',
        'Monthly/Quarterly Returns',
        'Annual GST Returns',
        'GST Audit & Reconciliation',
        'E-way Bill Management',
        'Input Tax Credit Optimization',
      ],
    },
    {
      icon: ShieldCheckIcon,
      title: 'Audit & Assurance',
      description: 'Professional audit services ensuring compliance and insights',
      shortDescription: 'Comprehensive audits for financial accuracy and regulatory compliance',
      features: [
        'Statutory Audit',
        'Internal Audit',
        'Tax Audit',
        'GST Audit',
        'Management Audit',
        'Forensic Audit',
      ],
    },
    {
      icon: BuildingOffice2Icon,
      title: 'Business Registration',
      description: 'Hassle-free company formation and legal compliance',
      shortDescription: 'Complete business setup services with ongoing compliance support',
      features: [
        'Private Limited Company',
        'LLP Registration',
        'Partnership Firm',
        'Proprietorship Setup',
        'One Person Company',
        'Section 8 Company',
      ],
    },
    {
      icon: ChartBarIcon,
      title: 'Financial Consulting',
      description: 'Strategic financial planning and business advisory',
      shortDescription: 'Data-driven insights for sustainable business growth',
      features: [
        'Business Valuation',
        'Financial Projections',
        'Cost Optimization',
        'Investment Analysis',
        'Working Capital Management',
        'Risk Assessment',
      ],
    },
  ];

  const serviceCategories = [
    {
      title: 'Individual Services',
      icon: UserGroupIcon,
      services: [
        'Income Tax Return Filing',
        'Tax Planning',
        'Investment Advisory',
        'NRI Services',
        'Property Tax',
      ],
    },
    {
      title: 'Business Services',
      icon: BuildingOffice2Icon,
      services: [
        'Company Registration',
        'GST Compliance',
        'Audit & Assurance',
        'Payroll Management',
        'Financial Reporting',
      ],
    },
    {
      title: 'Specialized Services',
      icon: GlobeAltIcon,
      services: [
        'International Taxation',
        'Transfer Pricing',
        'Forensic Audit',
        'Due Diligence',
        'Business Valuation',
      ],
    },
  ];

  const process = [
    {
      icon: ClipboardDocumentListIcon,
      title: 'Consultation',
      description: 'Free initial consultation to understand your needs',
    },
    {
      icon: PresentationChartLineIcon,
      title: 'Analysis',
      description: 'Detailed analysis of your financial situation',
    },
    {
      icon: ScaleIcon,
      title: 'Strategy',
      description: 'Custom strategy development for optimal outcomes',
    },
    {
      icon: BriefcaseIcon,
      title: 'Implementation',
      description: 'Seamless implementation with ongoing support',
    },
  ];

  return (
    <div>
      <MetaTags
        title="Our Services - Comprehensive Financial Solutions | NAQD Consulting"
        description="Explore complete range of chartered accountancy services including tax planning, GST compliance, business registration, audit services, and NRI taxation in Kerala."
        keywords="chartered accountant services malappuram, tax planning kerala, gst compliance services, business registration malappuram, nri tax services kerala, audit services"
      />

      {/* Hero Section */}
      <section
        className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/assets/images/backgrounds/accounting-hero.png')" }}
      >
        <div className="absolute inset-0 bg-naqd-primary/80"></div>
        <div className="container-custom text-center relative z-10">
          <div className="animate-on-scroll">
            <h1 className="heading-primary mb-6 text-white">
              Our Services
            </h1>
            <p className="text-xl text-gray-200 max-w-4xl mx-auto font-light">
              Comprehensive financial solutions tailored to your business needs.
              From compliance to strategic advisory, we've got you covered.
            </p>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="heading-secondary">Service Categories</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We've organized our services into clear categories to help you find exactly what you need.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {serviceCategories.map((category, index) => (
              <div
                key={index}
                className="card-hover bg-white p-8 rounded-2xl border border-gray-100 shadow-sm animate-on-scroll group"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-naqd-primary/5 rounded-full mb-6 group-hover:bg-naqd-primary group-hover:text-white transition-colors duration-300">
                  <category.icon className="w-8 h-8 text-naqd-primary group-hover:text-white" />
                </div>
                <h3 className="text-2xl font-semibold mb-6 text-naqd-primary">{category.title}</h3>
                <ul className="space-y-3">
                  {category.services.map((service, idx) => (
                    <li key={idx} className="text-gray-600 flex items-center">
                      <CheckCircleIcon className="w-5 h-5 text-naqd-secondary mr-3" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="section-padding bg-naqd-accent">
        <div className="container-custom">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="heading-secondary">Detailed Service Offerings</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore our complete range of services designed to drive your success.
            </p>
          </div>

          <div className="space-y-12">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 md:p-12 shadow-sm hover:shadow-xl transition-all duration-500 animate-on-scroll border border-gray-100"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                  <div className="lg:col-span-2">
                    <div className="flex items-start space-x-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-naqd-primary/5 rounded-none flex items-center justify-center">
                          <service.icon className="w-8 h-8 text-naqd-primary" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-3xl font-semibold mb-4 text-naqd-primary">{service.title}</h3>
                        <p className="text-gray-600 mb-6 text-lg leading-relaxed">{service.description}</p>
                        <p className="text-naqd-light-text-secondary mb-8 font-medium">{service.shortDescription}</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center">
                              <CheckCircleIcon className="w-5 h-5 text-naqd-secondary mr-3" />
                              <span className="text-gray-600">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-center items-center lg:items-end border-t lg:border-t-0 lg:border-l border-gray-100 pt-8 lg:pt-0 lg:pl-12">
                    <Link
                      to="/contact"
                      className="btn-primary w-full md:w-auto text-center"
                    >
                      Get Quote
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-naqd-primary text-white">
        <div className="container-custom">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="heading-secondary text-white">Our Process</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Simple, transparent process designed for your convenience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div
                key={index}
                className="text-center animate-on-scroll"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 rounded-full mb-6 backdrop-blur-sm">
                  <step.icon className="w-10 h-10 text-naqd-secondary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-gray-300 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-custom text-center">
          <div className="max-w-4xl mx-auto animate-on-scroll">
            <h2 className="heading-secondary mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-600 mb-10 font-light">
              Choose from our comprehensive range of services and experience the
              NAQD difference. Expert guidance, guaranteed results.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="hover-shine btn-primary rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
                Book Consultation
              </Link>
              <a href="tel:+919074286763" className="hover-shine btn-secondary rounded-full transform hover:-translate-y-1 transition-all duration-300">
                Call: +91 90742 86763
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;